import { ErrorBoundary, FlexBox, Spinner } from '@dixa/lib-shared-habla';
import {
  AgentInterfaceSuggestionsProps,
  KnowledgeSuggestionsEvents,
} from 'lib-knowledge-types';
import { authManager } from 'lib-shared-auth';
import { config } from 'lib-shared-backend-config';
import { ampli } from 'lib-shared-tracking';
import { Suspense, useMemo } from 'react';

import { RemoteKnowledgeSuggestions } from 'app/views/layout/views/knowledge/components/remotes';
import { actions as personalActions } from 'components/settings/actions/personal';
import { useNotifications } from 'hooks/useNotifications';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useCopilotTrial } from 'utils/useCopilotTrial';

import { KnowledgeErrorPlaceholder } from '../../../elevio/src/components/KnowledgeErrorPlaceholder';
import { knowledgeActions } from '../../knowledge.actions';

import { knowledgeSelectors } from './knowledgeSelectors';
import styles from './knowledgeSuggestions.module.scss';

type KnowledgeSuggestionsProps = Pick<
  AgentInterfaceSuggestionsProps,
  'isMinimized'
>;

export function KnowledgeSuggestions({
  isMinimized,
}: KnowledgeSuggestionsProps): JSX.Element | null {
  const conversationId = useAppSelector(knowledgeSelectors.conversation.id);
  const conversationTags = useAppSelector(knowledgeSelectors.conversation.tags);
  const conversationText = useAppSelector(knowledgeSelectors.conversation.text);
  const queueId = useAppSelector(knowledgeSelectors.conversation.queueId);

  const notify = useNotifications();
  const dispatch = useAppDispatch();
  const { hasCopilot } = useCopilotTrial();

  const { smartArticleCreate: hasSmartArticleCreate } = useFeatureFlag([
    'smart-article-create',
  ]);

  const knowledgeCardOpen = useAppSelector(
    (state) => state.settings.personal.knowledgeCardOpen,
  );

  function setKnowledgeCardOpen(newValue: boolean) {
    dispatch(
      personalActions.personalToggleRememberKnowledgeCardOpen({
        knowledgeCardOpen: newValue,
      }),
    );
  }

  const onHandlers = useMemo(() => {
    const handlers: KnowledgeSuggestionsEvents = {
      'conversation:smart-reply-requested': ({ articleId, conversationId }) => {
        ampli.smartConversationReplyButtonClicked({
          articleId,
          conversationId,
          knowledgeSource: 'dixa',
        });
        dispatch(knowledgeActions.smartConversationResponseRequested());
      },
      'conversation:smart-reply-response-received': ({ message }) => {
        dispatch(
          knowledgeActions.smartConversationResponseReceived({
            message,
          }),
        );
      },
      'conversation:smart-reply-response-error': ({ errorMessage }) => {
        dispatch(
          knowledgeActions.smartConversationResponseError({ errorMessage }),
        );
      },
      'conversation:smart-reply-response-finally': () => {
        dispatch(knowledgeActions.smartConversationResponseFinally());
      },
    };
    return handlers;
  }, []);

  if (!conversationId) return null;

  return (
    <ErrorBoundary fallback={<KnowledgeErrorPlaceholder />}>
      <Suspense
        fallback={
          <FlexBox className={styles.spinner} justifyContent="center">
            <Spinner />
          </FlexBox>
        }
      >
        <RemoteKnowledgeSuggestions
          conversationId={conversationId}
          conversationTags={conversationTags}
          conversationText={conversationText}
          notify={notify}
          environment={config.environment}
          getLock={async () => await authManager.getAuthData()}
          isMinimized={isMinimized}
          isOpen={knowledgeCardOpen}
          setIsOpen={setKnowledgeCardOpen}
          queueId={queueId}
          on={onHandlers}
          hasSmartReply={hasCopilot} // TODO - remove this prop, but cannot remove yet until `knowledge-fe` version using `featureFlags` is deployed to production
          featureFlags={{
            hasSmartReply: hasCopilot,
            hasSmartArticleCreate,
          }}
        />
      </Suspense>
    </ErrorBoundary>
  );
}
